import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FilterSelectionMode, getFilterSelectionMode } from 'state/filterTypes'
import { KodiakFarm } from 'types/farms'

import { allFarmFilters, FarmMemoryFilter } from './filters'
import { KodiakFarmOrderBy, OrderDirection } from './types'

export type TransitionType = 'sort' | 'filter' | 'search' | null

export interface KodiakFarmsState {
  kodiakFarms: KodiakFarm[]
  isLoading: boolean

  searchTerm: string
  orderBy: KodiakFarmOrderBy
  orderDirection: OrderDirection
  filters: FarmMemoryFilter[]

  offset: number
  limit: number

  dataTransition: boolean
  transitionType: TransitionType
}

export const initialState: KodiakFarmsState = {
  kodiakFarms: [],
  isLoading: false,

  searchTerm: '',
  orderBy: KodiakFarmOrderBy.FarmTVL,
  orderDirection: OrderDirection.Desc,
  filters: allFarmFilters,

  offset: 0,
  limit: 20,

  dataTransition: false,
  transitionType: null,
}

const kodiakFarmsSlice = createSlice({
  name: 'kodiakFarms',
  initialState,
  reducers: {
    updateKodiakFarms(state, { payload: { kodiakFarms } }: PayloadAction<{ kodiakFarms: KodiakFarm[] }>) {
      if (Array.isArray(kodiakFarms)) {
        state.kodiakFarms = kodiakFarms.filter(Boolean)
        state.isLoading = false
        state.dataTransition = false
        state.transitionType = null
      }
    },
    clearKodiakFarms(state) {
      state.kodiakFarms = []
      state.isLoading = true
    },

    setSearchTerm(state, { payload }: PayloadAction<string>) {
      const next = payload ?? ''
      if (state.searchTerm !== next) {
        state.searchTerm = next
        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'search'
        state.isLoading = true
      }
    },

    setSortBy(
      state,
      {
        payload: { orderBy, orderDirection },
      }: PayloadAction<{ orderBy: KodiakFarmOrderBy; orderDirection: OrderDirection }>
    ) {
      const changed = orderBy !== state.orderBy || orderDirection !== state.orderDirection
      state.orderBy = orderBy
      state.orderDirection = orderDirection
      if (changed) {
        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'sort'
        state.isLoading = true
      }
    },

    toggleFilter: (state, action: PayloadAction<string>) => {
      const filterDisplayName = action.payload
      const filter = state.filters.find((f) => f.displayName === filterDisplayName)

      if (filter) {
        const selectionMode = getFilterSelectionMode(filter)

        if (selectionMode === FilterSelectionMode.SINGLE) {
          // If clicking the already active filter, deactivate all in group
          if (filter.isActive) {
            state.filters = state.filters.map((f) => {
              if (f.group === filter.group) {
                return { ...f, isActive: false }
              }
              return f
            })
          } else {
            // Otherwise, activate this one and deactivate others in group
            state.filters = state.filters.map((f) => {
              if (f.group === filter.group) {
                return { ...f, isActive: f.displayName === filterDisplayName }
              }
              return f
            })
          }
        } else {
          // MULTI or TOGGLE mode: just toggle
          filter.isActive = !filter.isActive
        }

        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'filter'
        state.isLoading = true
      }
    },

    activateFilter(state, { payload: filter }: PayloadAction<FarmMemoryFilter>) {
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
      state.offset = 0

      if (filter.group) {
        state.filters = state.filters.map((f) => {
          if (f.group === filter.group) {
            return {
              displayName: f.displayName,
              isActive: f.displayName === filter.displayName,
              group: f.group,
              priority: f.priority,
            }
          }
          return f
        })
      } else {
        state.filters = state.filters.map((f) => {
          if (f.displayName === filter.displayName) {
            return {
              displayName: f.displayName,
              isActive: true,
              group: f.group,
              priority: f.priority,
            }
          }
          return f
        })
      }
    },

    deactivateFilter(state, { payload: filter }: PayloadAction<FarmMemoryFilter>) {
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
      state.offset = 0

      state.filters = state.filters.map((f) => {
        if (f.displayName === filter.displayName) {
          return {
            displayName: f.displayName,
            isActive: false,
            group: f.group,
            priority: f.priority,
          }
        }
        return f
      })
    },

    setFilter: (state, action: PayloadAction<{ displayName: string; isActive: boolean }>) => {
      const { displayName, isActive } = action.payload
      const filter = state.filters.find((f: { displayName: string }) => f.displayName === displayName)

      if (filter) {
        filter.isActive = isActive
        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'filter'
        state.isLoading = true
      }
    },

    resetFilters: (state) => {
      state.filters = allFarmFilters.map((f: any) => ({ ...f, isActive: false }))
      state.offset = 0
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
    },

    resetFilterState(state) {
      state.searchTerm = ''
      state.orderBy = KodiakFarmOrderBy.FarmTVL
      state.orderDirection = OrderDirection.Desc
      state.filters = allFarmFilters.map((f: any) => ({ ...f, isActive: false }))
      state.offset = 0
      state.dataTransition = true
      state.transitionType = 'filter'
      state.isLoading = true
    },

    setKodiakFarmPagination(state, { payload: { offset, limit } }: PayloadAction<{ offset?: number; limit?: number }>) {
      const pagingChanged =
        (typeof offset === 'number' && offset !== state.offset) || (typeof limit === 'number' && limit !== state.limit)

      if (typeof offset === 'number') state.offset = offset
      if (typeof limit === 'number') state.limit = limit

      if (pagingChanged) {
        state.dataTransition = true
        state.transitionType = null
        state.isLoading = true
      }
    },

    updateQueryParams(
      state,
      {
        payload: { orderBy, orderDirection, offset, limit },
      }: PayloadAction<{
        orderBy?: KodiakFarmOrderBy
        orderDirection?: OrderDirection
        offset?: number
        limit?: number
      }>
    ) {
      const sortWillChange =
        (orderBy !== undefined && orderBy !== state.orderBy) ||
        (orderDirection !== undefined && orderDirection !== state.orderDirection)

      if (orderBy !== undefined) state.orderBy = orderBy
      if (orderDirection !== undefined) state.orderDirection = orderDirection

      if (sortWillChange) {
        state.offset = 0
        state.dataTransition = true
        state.transitionType = 'sort'
        state.isLoading = true
      }

      if (limit !== undefined) state.limit = limit
      if (offset !== undefined) state.offset = offset

      if (!sortWillChange && (limit !== undefined || offset !== undefined)) {
        state.dataTransition = true
        state.transitionType = null
        state.isLoading = true
      }
    },

    setFarmsLoading(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload
    },

    setDataTransition(
      state,
      { payload: { inTransition, type } }: PayloadAction<{ inTransition: boolean; type: TransitionType }>
    ) {
      state.dataTransition = inTransition
      state.transitionType = inTransition ? type : null
      if (inTransition) state.isLoading = true
    },

    endDataTransition(state) {
      state.dataTransition = false
      state.transitionType = null
    },
  },
})

export const {
  updateKodiakFarms,
  clearKodiakFarms,
  setSearchTerm,
  setSortBy,
  toggleFilter,
  activateFilter,
  deactivateFilter,
  setFilter,
  resetFilters,
  resetFilterState,
  setKodiakFarmPagination,
  updateQueryParams,
  setFarmsLoading,
  setDataTransition,
  endDataTransition,
} = kodiakFarmsSlice.actions

export default kodiakFarmsSlice.reducer

export const selectKodiakFarmsState = (s: { kodiakFarms: KodiakFarmsState }) => s.kodiakFarms
export const selectFarmQueryArgs = (s: { kodiakFarms: KodiakFarmsState }) => {
  const st = s.kodiakFarms
  return {
    searchTerm: st.searchTerm,
    orderBy: st.orderBy,
    orderDirection: st.orderDirection,
    offset: st.offset,
    limit: st.limit,
  }
}
